import axios from "axios";
import {
  getMainBackendBaseHeaders,
  getLegacyBearerTokenFromStorage,
} from "../utils/mainBackendRequestConfig";
import {
  isOfflineError,
  getOfflineMessage,
  getOfflineSubmissionMessage,
} from "../utils/programming/offlineHandler";
import { indexedDBUploadManager } from "../utils/programming/indexedDBUploadManager";
import { offlineModeManager } from "../utils/programming/offlineModeManager";
import { textAnswerRetryManager } from "../utils/programming/textAnswerRetryManager";
import { isMobileDevice } from "../utils/programming/deviceDetection.util";

const PUBLIC_ASSESSMENT_HEADERS = {
  "ngrok-skip-browser-warning": true,
};

/**
 * Unified API Service Class
 * Provides a consistent interface for all API calls with advanced features:
 * - Request cancellation
 * - Offline detection and queueing
 * - Mobile optimization
 * - Progress tracking
 * - Comprehensive error handling
 */
class APIService {
  constructor(baseURL) {
    this.baseURL = baseURL;
    this.ongoingRequests = {};
  }

  /**
   * Setup request cancellation
   * @param {string} endpoint - API endpoint
   * @param {string} method - HTTP method
   * @param {string} requestId - Optional unique request ID
   * @returns {CancelToken} Axios cancel token
   */
  setupCancellation(endpoint, method, requestId) {
    const key = requestId || `${endpoint}_${method}`;

    // Cancel any ongoing request with the same key
    if (this.ongoingRequests[key]) {
      this.ongoingRequests[key].cancel("Cancelled due to new request");
    }

    this.ongoingRequests[key] = axios.CancelToken.source();
    return this.ongoingRequests[key].token;
  }

  /**
   * Clean up request tracking
   * @param {string} endpoint - API endpoint
   * @param {string} method - HTTP method
   * @param {string} requestId - Optional unique request ID
   */
  cleanupRequest(endpoint, method, requestId) {
    const key = requestId || `${endpoint}_${method}`;
    delete this.ongoingRequests[key];
  }

  /**
   * Check if API call should be queued for offline retry
   * @param {string} endpoint - API endpoint
   * @param {boolean} queueable - Explicit queueable flag
   * @returns {boolean}
   */
  shouldQueueAPICall(endpoint, queueable) {
    if (queueable !== undefined) return queueable;

    const queueableEndpoints = [
      "update-candidate-result",
      "generate-upload-url",
      "submit-candidate-result",
      "submit-assessment",
      "programming/draft",
      "editor-events",
    ];

    return queueableEndpoints.some((pattern) => endpoint.includes(pattern));
  }

  /**
   * Extract answer data for offline queueing
   * @param {string} endpoint - API endpoint
   * @param {string} method - HTTP method
   * @param {Object} data - Request payload
   * @param {string} candidateAssessmentId - Candidate assessment ID
   * @returns {Object|null}
   */
  extractAnswerData(endpoint, method, data, candidateAssessmentId) {
    // For updateCandidateResult
    if (endpoint.includes("/update-candidate-result")) {
      return {
        questionId: data?.questionId,
        questionType: data?.type,
        payload: data,
        apiUrl: `${this.baseURL}${endpoint}`,
        apiMethod: method,
        candidateAssessmentId:
          candidateAssessmentId || data?.candidateAssessmentId,
      };
    }

    // For submitCandidateResult (final submission)
    if (
      endpoint.includes("/submit-candidate-result") ||
      endpoint.includes("/submit-assessment")
    ) {
      return {
        questionId: `SUBMISSION_${candidateAssessmentId || data?.candidateAssessmentId
          }`,
        isFinalSubmission: true,
        payload: data,
        assessmentId: data?.assessmentId || candidateAssessmentId,
        apiUrl: `${this.baseURL}${endpoint}`,
        apiMethod: method,
        candidateAssessmentId:
          candidateAssessmentId || data?.candidateAssessmentId,
      };
    }

    return null;
  }

  /**
   * Queue request for offline retry
   * @param {Object} context - Request context
   * @returns {Promise<Object>}
   */
  async queueRequest(context) {
    const { endpoint, method, data } = context;

    // Extract candidateAssessmentId from URL or data
    let candidateAssessmentId = data?.candidateAssessmentId;

    if (!candidateAssessmentId) {
      const urlMatch = endpoint.match(/\/([a-f0-9]{24})\//);
      if (urlMatch && urlMatch[1]) {
        candidateAssessmentId = urlMatch[1];
      }
    }

    const answerData = this.extractAnswerData(
      endpoint,
      method,
      data,
      candidateAssessmentId,
    );

    if (!answerData) {
      throw new Error("Cannot queue this request - no answer data extracted");
    }

    // Handle final submission
    if (answerData.isFinalSubmission && answerData.candidateAssessmentId) {
      await indexedDBUploadManager.storePendingSubmission(
        answerData.candidateAssessmentId,
        {
          payload: answerData.payload,
          assessmentId: answerData.assessmentId,
        },
      );

      return {
        queued: true,
        message: getOfflineSubmissionMessage(),
        status: 202,
      };
    }

    // Handle answer submission
    if (answerData.questionId && answerData.candidateAssessmentId) {
      const questionType = answerData.questionType || answerData.payload?.type;

      const completeAnswerData = {
        payload: answerData.payload,
        questionType: answerData.questionType,
        apiUrl: answerData.apiUrl,
        apiMethod: answerData.apiMethod,
        candidateAssessmentId: answerData.candidateAssessmentId,
        typingAnalysis: answerData.payload?.typingAnalysis || null,
        copyPasteAnalysis: answerData.payload?.copyPasteAnalysis || null,
        cheatingCounts: {
          fullScreenExitCount: answerData.payload?.fullScreenExitCount || 0,
          tabSwitchCount: answerData.payload?.tabSwitchCount || 0,
        },
      };

      await indexedDBUploadManager.storePendingAnswer(
        answerData.questionId,
        completeAnswerData,
      );

      // Start background retry for non-video/audio questions
      if (questionType !== "video" && questionType !== "audio") {
        const { updateCandidateResult } = await import("./assessment.api");
        textAnswerRetryManager.startBackgroundRetry(
          answerData.questionId,
          completeAnswerData,
          (assessmentId, payload) =>
            updateCandidateResult(assessmentId, payload),
        );
      }

      return {
        queued: true,
        message: getOfflineMessage(),
        questionId: answerData.questionId,
        status: 202,
      };
    }

    throw new Error("Missing required fields for queueing");
  }

  /**
   * Handle API errors with offline support
   * @param {Error} error - Error object
   * @param {Object} context - Request context
   * @returns {Promise}
   */
  async handleError(error, context) {
    const { endpoint, method, requestId } = context;

    // Clean up request tracking
    this.cleanupRequest(endpoint, method, requestId);

    // Don't reject for canceled requests
    if (axios.isCancel(error)) {
      const cancelError = new Error("Request cancelled");
      cancelError.cancelled = true;
      cancelError.isCancel = true; // For broader compatibility
      return Promise.reject(cancelError);
    }

    // Check if error is offline-related
    const isOffline = isOfflineError(error);

    // Auto-queue if offline and queueable
    if (isOffline && this.shouldQueueAPICall(endpoint, context.queueable)) {
      if (offlineModeManager.isOfflineModeSupported()) {
        try {
          const result = await this.queueRequest(context);
          return Promise.resolve(result);
        } catch (queueError) {
          console.error("❌ Error queueing request:", queueError);
          if (error && typeof error === "object") {
            error.queueError = queueError;
          }
        }
      } else {
        if (error && typeof error === "object") {
          error.isOffline = true;
          error.blockNavigation = true;
          error.userMessage =
            "Cannot submit while offline. Please check your connection and try again.";
        }
      }
    }

    // Add offline flag to error
    if (isOffline && error && typeof error === "object") {
      error.isOffline = true;
    }

    // Enhanced error logging (only for real errors, not ResizeObserver)
    if (error?.message && !error.message.includes("ResizeObserver")) {
      console.error("🔴 API Error:", {
        endpoint,
        method,
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.message,
        isOffline,
        data: error.response?.data,
      });
    } else if (!error?.message) {
      console.error("🔴 API Error (Unknown):", error);
    }

    return Promise.reject(error);
  }

  /**
   * Core request method
   * @param {Object} options - Request options
   * @returns {Promise}
   */
  async request({
    endpoint,
    method = "GET",
    data = null,
    headers = {},
    auth = false,
    timeout = 30000,
    requestId = null,
    queueable = undefined,
    onProgress = null,
    params = null,
  }) {
    const url = `${this.baseURL}${endpoint}`;

    // Public `/public/assessment/*` (default): no cookies / portal / CSRF.
    // `auth: true`: session + CSRF + withCredentials (hire360-app parity).
    let withCredentials = false;
    if (auth) {
      const legacy = getLegacyBearerTokenFromStorage();
      const baseHeaders = getMainBackendBaseHeaders();
      headers = {
        ...baseHeaders,
        ...(legacy ? { Authorization: `Bearer ${legacy}` } : {}),
        ...headers,
      };
      withCredentials = true;
    } else {
      headers = { ...PUBLIC_ASSESSMENT_HEADERS, ...headers };
    }

    // Mobile optimization - use longer timeout
    const isMobile = isMobileDevice();
    const effectiveTimeout = isMobile && timeout < 120000 ? 120000 : timeout;

    // Setup request cancellation
    const cancelToken = this.setupCancellation(endpoint, method, requestId);

    try {
      const config = {
        url,
        method: method.toUpperCase(),
        headers,
        withCredentials,
        timeout: effectiveTimeout,
        cancelToken,
      };

      // Add data for POST, PUT, PATCH
      if (data && ["POST", "PUT", "PATCH"].includes(method.toUpperCase())) {
        config.data = data;
      }

      // Add query params for GET
      if (params) {
        config.params = params;
      }

      // Add progress tracking
      if (onProgress) {
        config.onUploadProgress = onProgress;
      }

      const response = await axios(config);

      // Clean up request tracking
      this.cleanupRequest(endpoint, method, requestId);

      return response.data;
    } catch (error) {
      return this.handleError(error, {
        endpoint,
        method,
        data,
        requestId,
        queueable,
      });
    }
  }

  /**
   * Convenience method for GET requests
   */
  get(endpoint, options = {}) {
    return this.request({ endpoint, method: "GET", ...options });
  }

  /**
   * Convenience method for POST requests
   */
  post(endpoint, data, options = {}) {
    return this.request({ endpoint, method: "POST", data, ...options });
  }

  /**
   * Convenience method for PATCH requests
   */
  patch(endpoint, data, options = {}) {
    return this.request({ endpoint, method: "PATCH", data, ...options });
  }

  /**
   * Convenience method for PUT requests
   */
  put(endpoint, data, options = {}) {
    return this.request({ endpoint, method: "PUT", data, ...options });
  }

  /**
   * Convenience method for DELETE requests
   */
  delete(endpoint, options = {}) {
    return this.request({ endpoint, method: "DELETE", ...options });
  }

  /**
   * Callback-style request (for backward compatibility with handleDynamicRequest)
   * @param {string} method - HTTP method
   * @param {string} url - Full URL (will extract endpoint)
   * @param {Object} requestBody - Request body
   * @param {Function} wrongResponse - Error callback
   * @param {Function} rightResponse - Success callback
   * @param {Function} onNotFound - 404 callback (optional)
   * @param {Object} options - Additional options
   */
  async requestWithCallbacks(
    method,
    url,
    requestBody,
    wrongResponse,
    rightResponse,
    onNotFound = null,
    options = {},
  ) {
    try {
      // Extract endpoint from full URL
      const endpoint = url.replace(this.baseURL, "");

      const response = await this.request({
        endpoint,
        method,
        data: requestBody,
        auth: options.auth || false,
        headers: options.headers || {},
        timeout: options.timeout || 30000,
        queueable: options.queueable,
      });

      // Call success callback with a slight delay (matching original behavior)
      setTimeout(() => {
        if (rightResponse) {
          rightResponse(response);
        }
      }, 100);
    } catch (error) {
      // Handle 404 specifically
      if (error.response?.status === 404 && onNotFound) {
        setTimeout(() => {
          onNotFound();
        }, 100);
        return;
      }

      // Call error callback
      setTimeout(() => {
        if (wrongResponse) {
          wrongResponse(error.response?.data || error);
        }
      }, 100);
    }
  }

  /**
   * Callback-style request with authorization header
   * @param {string} method - HTTP method
   * @param {string} url - Full URL
   * @param {Object} requestBody - Request body
   * @param {string} token - Authorization token
   * @param {Function} wrongResponse - Error callback
   * @param {Function} rightResponse - Success callback
   */
  async requestWithAuth(
    method,
    url,
    requestBody,
    token,
    wrongResponse,
    rightResponse,
  ) {
    try {
      const endpoint = url.replace(this.baseURL, "");

      const headers = {
        "Content-Type": "application/json",
      };

      if (token) {
        headers.Authorization = token;
      }

      const response = await this.request({
        endpoint,
        method,
        data: requestBody,
        headers,
        auth: false, // We're manually adding the token
      });

      if (rightResponse && response) {
        rightResponse(response);
      }
    } catch (error) {
      // Don't call error callback if request was cancelled
      if (wrongResponse && !error.cancelled && !axios.isCancel(error)) {
        wrongResponse(error.response?.data || error);
      }
    }
  }
}

export default APIService;
